import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Modal,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { useTheme } from '../../contexts/ThemeContext';
import { DayApplyScopePicker } from '../DayApplyScopePicker';
import { CalendarWriterEditor } from './CalendarWriterEditor';
import { TourPartyMemberChip } from './TourPartyMemberChip';
import { TourPartyMemberCard } from './TourPartyMemberCard';
import { RiderShareBanner } from './RiderShareBanner';
import { ThemedSwitch } from './ThemedSwitch';
import { useCalendarGlassStyles } from './calendarGlassStyles';
import { useTourPartyMembers } from '../../hooks/useTourPartyMembers';
import { buildTourPartyMemberBlock, parseWriterMemberBlocks } from '../../utils/writerFormat';
import {
  getWriterEditorHtml,
  insertWriterMember,
  removeWriterMember,
  writerMemberExistsInEditor,
} from '../../utils/writerEditorApi';
import { normalizeEmail, resolveMemberLabel, tourPartyMemberBlockId } from '../../utils/tourPartyMemberUtils';
import { importDocumentAssetToHtml, importDocumentAssetToPlainText } from '../../utils/documentImport';
import { formatRiderDocumentWithAI } from '../../utils/riderAiFormat';
import {
  WRITER_FORMAT_ACTIONS,
  runWriterFormatCommand,
  insertWriterHyperlink,
  insertWriterLinkButton,
  insertWriterHtml,
} from '../../utils/writerCommands';
import { openRiderPrintWindow } from '../../utils/riderPdfExport';
import {
  buildRiderDayShareLink,
  sendRiderToEmails,
  getFirestoreDb,
} from '../../utils/riderShareService';
import RiderEmailRecipientsModal from './RiderEmailRecipientsModal';

const RIDER_MODES = [
  { id: 'DAILY', label: 'Daily', hint: 'Per-show-day rider notes' },
  { id: 'FULL', label: 'Full', hint: 'Full rider document for this scope' },
];

const FORMAT_ACTION_ICONS = {
  bullet: 'list-outline',
};

const FORMAT_ACTION_SHORT_LABELS = {
  alignLeft: 'L',
  alignCenter: 'C',
  alignRight: 'R',
};

function resolveAppBaseUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'https://venuereportcard.com';
}

function formatDateLabel(dateKey) {
  const parts = String(dateKey || '').split('-').map(Number);
  if (parts.length !== 3) return dateKey || '';
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

export function CalendarWriterPanel({
  selectedDateKey,
  value,
  onChange,
  riderMode = 'DAILY',
  onRiderModeChange,
  tabApplyScope,
  setTabApplyScope,
  selectedTourShowDateKeys = [],
  onSave,
  saving = false,
  ownerId,
  ownerName,
  ownerEmail,
  ownerPhotoURL,
  tourId,
  tourName,
  theme,
  onError,
  onSuccess,
  sidebarWide = false,
  riderShares = [],
}) {
  const editorRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [importChooserOpen, setImportChooserOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [linkModal, setLinkModal] = useState({ open: false, url: '', label: '' });
  const [buttonModal, setButtonModal] = useState({ open: false, url: '', label: '' });
  const [printModal, setPrintModal] = useState({ open: false, includeLinkUrls: true });
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const { members, loading: membersLoading } = useTourPartyMembers({
    ownerId,
    ownerName,
    ownerEmail,
    ownerPhotoURL,
    tourId,
  });
  const glassStyles = useCalendarGlassStyles(theme, isDarkMode);
  const styles = useMemo(() => createStyles(theme, glassStyles), [theme, glassStyles]);

  const tabKey = riderMode === 'FULL' ? 'RIDER_FULL' : 'RIDER_DAILY';
  const dateLabel = useMemo(() => formatDateLabel(selectedDateKey), [selectedDateKey]);
  const shareLink = useMemo(() => {
    if (!ownerId || !tourId || !selectedDateKey) return '';
    return buildRiderDayShareLink({
      baseUrl: resolveAppBaseUrl(),
      ownerId,
      tourId,
      dateKey: selectedDateKey,
    });
  }, [ownerId, tourId, selectedDateKey]);

  const syncEditor = useCallback(() => {
    const editor = editorRef.current;
    if (editor) onChange?.(getWriterEditorHtml(editor));
  }, [onChange]);

  const runFormatCommand = useCallback(
    (action) => {
      if (Platform.OS !== 'web') return;
      runWriterFormatCommand(editorRef.current, action, onChange);
    },
    [onChange]
  );

  const insertMember = useCallback(
    (member) => {
      const label = resolveMemberLabel(member, 'Tour member');
      if (Platform.OS === 'web') {
        const editor = editorRef.current;
        if (!editor) return;
        if (writerMemberExistsInEditor(editor, member)) {
          onError?.(`${label} is already in this rider.`);
          return;
        }
        insertWriterMember(editor, member);
        return;
      }
      onChange?.(`${String(value || '')}${buildTourPartyMemberBlock(member)}`);
    },
    [onChange, onError, value]
  );

  const removeMemberFromEditor = useCallback(
    (member) => {
      if (Platform.OS !== 'web') return;
      const editor = editorRef.current;
      if (!editor || !member) return;
      removeWriterMember(editor, member);
      syncEditor();
    },
    [syncEditor]
  );

  const insertedMembers = useMemo(() => {
    const blocks = parseWriterMemberBlocks(value);
    if (!blocks.length) return [];
    const byBlockId = new Map();
    const byUid = new Map();
    const byEmail = new Map();
    members.forEach((m) => {
      byBlockId.set(tourPartyMemberBlockId(m), m);
      if (m.id) byUid.set(String(m.id), m);
      if (m.email) byEmail.set(normalizeEmail(m.email), m);
    });
    return blocks
      .map((block) => {
        const full =
          byBlockId.get(block.id) ||
          byUid.get(block.id) ||
          byEmail.get(normalizeEmail(block.id)) ||
          (block.email ? byEmail.get(block.email) : null);
        if (full) return { ...full, name: resolveMemberLabel(full, block.name) };
        return {
          id: block.id,
          name: resolveMemberLabel({ name: block.name, email: block.email }, block.name || block.id),
          email: block.email || '',
        };
      })
      .filter((m, index, arr) => {
        const key = tourPartyMemberBlockId(m);
        return arr.findIndex((x) => tourPartyMemberBlockId(x) === key) === index;
      });
  }, [members, value]);

  const insertedMemberKeys = useMemo(
    () => new Set(insertedMembers.map((m) => tourPartyMemberBlockId(m))),
    [insertedMembers]
  );

  const insertImportedHtml = useCallback(
    (html) => {
      const editor = editorRef.current;
      if (Platform.OS === 'web' && editor) {
        insertWriterHtml(editor, html, onChange);
        return;
      }
      onChange?.(`${String(value || '')}${html}`);
    },
    [onChange, value]
  );

  const runDocumentImport = useCallback(
    async (mode) => {
      setImportChooserOpen(false);
      try {
        setImporting(true);
        setImportStatus('Opening file picker…');
        const result = await DocumentPicker.getDocumentAsync({
          type: [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/msword',
          ],
          copyToCacheDirectory: true,
          multiple: false,
        });
        if (result.canceled || !result.assets?.[0]) return;

        let html = '';
        if (mode === 'ai') {
          setImportStatus('Reading document…');
          const plainText = await importDocumentAssetToPlainText(result.assets[0]);
          setImportStatus('Analyzing and reformatting with AI…');
          html = await formatRiderDocumentWithAI(plainText, { tourName });
        } else {
          setImportStatus('Extracting document text…');
          html = await importDocumentAssetToHtml(result.assets[0]);
        }

        if (!html) {
          onError?.('No readable text was found in that document.');
          return;
        }

        insertImportedHtml(html);
        onSuccess?.(
          mode === 'ai'
            ? 'Document reformatted and imported into the writer.'
            : 'Document imported into the writer.'
        );
      } catch (err) {
        console.error('writer document import', err);
        onError?.(err?.message || 'Could not import document.');
      } finally {
        setImporting(false);
        setImportStatus('');
      }
    },
    [insertImportedHtml, onError, onSuccess, tourName]
  );

  const handleInsertLink = useCallback(() => {
    if (Platform.OS !== 'web') return;
    insertWriterHyperlink(editorRef.current, linkModal.url, linkModal.label, onChange);
    setLinkModal({ open: false, url: '', label: '' });
  }, [linkModal.label, linkModal.url, onChange]);

  const handleInsertButton = useCallback(() => {
    if (Platform.OS !== 'web') return;
    insertWriterLinkButton(editorRef.current, buttonModal.url, buttonModal.label, onChange);
    setButtonModal({ open: false, url: '', label: '' });
  }, [buttonModal.label, buttonModal.url, onChange]);

  const handlePrintRider = useCallback(() => {
    try {
      openRiderPrintWindow({
        html: value,
        title: tourName || 'Hospitality Rider',
        subtitle: dateLabel,
        includeLinkUrls: printModal.includeLinkUrls,
      });
      setPrintModal({ open: false, includeLinkUrls: true });
    } catch (err) {
      onError?.(err?.message || 'Could not open print view.');
    }
  }, [dateLabel, onError, printModal.includeLinkUrls, tourName, value]);

  const handleSendRiderEmail = useCallback(
    async (recipientQueue, note) => {
      if (!recipientQueue?.length) {
        onError?.('Add at least one recipient.');
        return;
      }
      if (!ownerId || !tourId || !selectedDateKey) {
        onError?.('Select a tour and day before sending.');
        return;
      }
      try {
        setSendingEmail(true);
        await sendRiderToEmails({
          db: getFirestoreDb(),
          ownerId,
          ownerName,
          recipients: recipientQueue,
          tourId,
          tourName,
          dateKey: selectedDateKey,
          dateLabel,
          tabKey,
          riderHtml: value,
          shareLink,
          message: note,
        });
        setEmailModalOpen(false);
        onSuccess?.(
          `Rider sent to ${recipientQueue.length} recipient${recipientQueue.length === 1 ? '' : 's'}.`
        );
      } catch (err) {
        onError?.(err?.message || 'Could not send rider email.');
      } finally {
        setSendingEmail(false);
      }
    },
    [
      dateLabel,
      onError,
      onSuccess,
      ownerId,
      ownerName,
      selectedDateKey,
      shareLink,
      tabKey,
      tourId,
      tourName,
      value,
    ]
  );

  const modeHint =
    riderMode === 'FULL'
      ? 'Full rider — use Copy to when the same document should appear on multiple show days.'
      : 'Daily rider — day-specific hospitality, dietary, and rooming notes.';

  return (
    <View style={styles.wrap}>
      <RiderShareBanner shares={riderShares} theme={theme} isDarkMode={isDarkMode} />

      <View style={styles.actionRow}>
        <TouchableOpacity style={styles.actionBtn} onPress={() => setEmailModalOpen(true)}>
          <Ionicons name="mail-outline" size={15} color={theme.brand.primary} />
          <Text style={styles.actionBtnText}>Email rider</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionBtn} onPress={() => setPrintModal({ open: true, includeLinkUrls: true })}>
          <Ionicons name="print-outline" size={15} color={theme.brand.primary} />
          <Text style={styles.actionBtnText}>Print PDF</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionBtn, importing && { opacity: 0.6 }]}
          onPress={() => setImportChooserOpen(true)}
          disabled={importing}
        >
          {importing ? (
            <ActivityIndicator size="small" color={theme.brand.primary} />
          ) : (
            <>
              <Ionicons name="cloud-upload-outline" size={15} color={theme.brand.primary} />
              <Text style={styles.actionBtnText}>Import</Text>
            </>
          )}
        </TouchableOpacity>
      </View>

      <View style={styles.modeRow}>
        {RIDER_MODES.map((mode) => {
          const active = riderMode === mode.id;
          return (
            <TouchableOpacity
              key={mode.id}
              style={[styles.modeBtn, active && styles.modeBtnActive]}
              onPress={() => onRiderModeChange?.(mode.id)}
            >
              <Text style={[styles.modeBtnText, active && styles.modeBtnTextActive]}>{mode.label}</Text>
              <Text style={[styles.modeBtnHint, active && styles.modeBtnHintActive]} numberOfLines={2}>
                {mode.hint}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <Text style={styles.lead}>{modeHint}</Text>

      <View style={styles.toolbarWrap}>
        <View style={styles.toolbar}>
          {WRITER_FORMAT_ACTIONS.map((action) => {
            const iconName = FORMAT_ACTION_ICONS[action.id];
            return (
              <TouchableOpacity
                key={action.id}
                style={[styles.toolBtn, iconName && styles.toolBtnIconOnly]}
                onPress={() => runFormatCommand(action)}
                accessibilityLabel={action.title || `Format ${action.label}`}
              >
                {iconName ? (
                  <Ionicons name={iconName} size={14} color={theme.text.primary} />
                ) : (
                  <Text
                    style={[
                      styles.toolBtnText,
                      ['bold', 'italic', 'underline', 'strike'].includes(action.id) && styles.toolBtnTextEm,
                    ]}
                  >
                    {FORMAT_ACTION_SHORT_LABELS[action.id] || action.label}
                  </Text>
                )}
              </TouchableOpacity>
            );
          })}
          <TouchableOpacity
            style={styles.toolBtn}
            onPress={() => setLinkModal({ open: true, url: 'https://', label: '' })}
            accessibilityLabel="Insert link"
          >
            <Ionicons name="link-outline" size={14} color={theme.text.primary} />
            <Text style={styles.toolBtnText}>Link</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.toolBtn}
            onPress={() => setButtonModal({ open: true, url: 'https://', label: 'Open link' })}
            accessibilityLabel="Insert button"
          >
            <Ionicons name="open-outline" size={14} color={theme.text.primary} />
            <Text style={styles.toolBtnText}>Btn</Text>
          </TouchableOpacity>
        </View>
      </View>
      {importStatus ? (
        <Text style={styles.importStatus}>{importStatus}</Text>
      ) : null}
      <Text style={styles.shortcutHint}>Shortcuts: ⌘B bold · ⌘I italic · ⌘U underline · ⌘⇧S strike</Text>

      <View style={styles.partySection}>
        <Text style={styles.sectionLabel}>Add tour party</Text>
        {membersLoading ? (
          <ActivityIndicator color={theme.brand.accent} size="small" />
        ) : members.length === 0 ? (
          <Text style={styles.sectionHint}>Invite tour party members on the Tour Party tab first.</Text>
        ) : (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.partyScroll}>
            {members.map((member) => {
              const blockId = tourPartyMemberBlockId(member);
              const alreadyInRider = insertedMemberKeys.has(blockId);
              return (
                <TourPartyMemberChip
                  key={member.inviteId || blockId}
                  member={member}
                  theme={theme}
                  isDarkMode={isDarkMode}
                  selected={alreadyInRider}
                  showAddBadge={!alreadyInRider}
                  onPress={alreadyInRider ? undefined : () => insertMember(member)}
                />
              );
            })}
          </ScrollView>
        )}
      </View>

      {insertedMembers.length > 0 ? (
        <View style={glassStyles.card}>
          <Text style={styles.sectionLabel}>In this rider</Text>
          <View style={styles.insertedMemberList}>
            {insertedMembers.map((member) => (
              <TourPartyMemberCard
                key={`inserted-${tourPartyMemberBlockId(member)}`}
                member={member}
                theme={theme}
                isDarkMode={isDarkMode}
                compact
                trailing={
                  <TouchableOpacity
                    onPress={() => removeMemberFromEditor(member)}
                    hitSlop={8}
                    accessibilityLabel={`Remove ${resolveMemberLabel(member)} from rider`}
                  >
                    <Ionicons name="close-circle" size={20} color={theme.status.error} />
                  </TouchableOpacity>
                }
              />
            ))}
          </View>
        </View>
      ) : null}

      <CalendarWriterEditor
        editorRef={editorRef}
        value={value}
        onChange={onChange}
        placeholder={
          riderMode === 'FULL'
            ? 'Write or import your full rider document…'
            : 'Write daily rider notes for this show day…'
        }
        theme={theme}
        isDarkMode={isDarkMode}
        editorMinHeight={sidebarWide ? 280 : 220}
        editorMaxHeight={sidebarWide ? 560 : 420}
      />

      {selectedDateKey ? (
        <View style={styles.scopeWrap}>
          <DayApplyScopePicker
            compact
            contained
            showSaveButton
            value={tabApplyScope}
            onChange={setTabApplyScope}
            anchorDateKey={selectedDateKey}
            tourShowDateKeys={selectedTourShowDateKeys}
            theme={theme}
            onSave={onSave}
            saving={saving}
          />
        </View>
      ) : null}

      <WriterInputModal
        visible={linkModal.open}
        title="Insert hyperlink"
        fields={[
          { key: 'url', label: 'URL', value: linkModal.url, placeholder: 'https://…' },
          { key: 'label', label: 'Link text (optional)', value: linkModal.label, placeholder: 'Display text' },
        ]}
        confirmLabel="Insert link"
        theme={theme}
        onClose={() => setLinkModal({ open: false, url: '', label: '' })}
        onChange={(key, next) => setLinkModal((prev) => ({ ...prev, [key]: next }))}
        onConfirm={handleInsertLink}
      />

      <WriterInputModal
        visible={buttonModal.open}
        title="Insert link button"
        fields={[
          { key: 'url', label: 'URL', value: buttonModal.url, placeholder: 'https://…' },
          { key: 'label', label: 'Button label', value: buttonModal.label, placeholder: 'Open link' },
        ]}
        confirmLabel="Insert button"
        theme={theme}
        onClose={() => setButtonModal({ open: false, url: '', label: '' })}
        onChange={(key, next) => setButtonModal((prev) => ({ ...prev, [key]: next }))}
        onConfirm={handleInsertButton}
      />

      <Modal visible={printModal.open} transparent animationType="fade" onRequestClose={() => setPrintModal({ open: false, includeLinkUrls: true })}>
        <Pressable style={styles.modalBackdrop} onPress={() => setPrintModal({ open: false, includeLinkUrls: true })}>
          <Pressable style={[styles.modalCard, { backgroundColor: theme.background.card, borderColor: theme.ui.border }]} onPress={() => {}}>
            <Text style={[styles.modalTitle, { color: theme.text.primary }]}>Print rider as PDF</Text>
            <Text style={[styles.modalHint, { color: theme.text.secondary }]}>
              Opens a print-ready page. Choose “Save as PDF” in the print dialog.
            </Text>
            <View style={styles.switchRow}>
              <Text style={[styles.switchLabel, { color: theme.text.primary }]}>Show link URLs in print</Text>
              <ThemedSwitch
                value={printModal.includeLinkUrls}
                onValueChange={(next) => setPrintModal((prev) => ({ ...prev, includeLinkUrls: next }))}
              />
            </View>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalBtnGhost} onPress={() => setPrintModal({ open: false, includeLinkUrls: true })}>
                <Text style={{ color: theme.text.secondary, fontWeight: '700' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalBtnPrimary, { backgroundColor: theme.brand.primary }]} onPress={handlePrintRider}>
                <Text style={{ color: theme.button.primaryText, fontWeight: '800' }}>Print</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <RiderEmailRecipientsModal
        visible={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSend={(queue, note) => void handleSendRiderEmail(queue, note)}
        sending={sendingEmail}
        ownerId={ownerId}
        tourId={tourId}
        selectedDateKey={selectedDateKey}
        tourPartyMembers={members}
        theme={theme}
      />

      <Modal
        visible={importChooserOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setImportChooserOpen(false)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setImportChooserOpen(false)}>
          <Pressable
            style={[styles.modalCard, { backgroundColor: theme.background.card, borderColor: theme.ui.border }]}
            onPress={() => {}}
          >
            <Text style={[styles.modalTitle, { color: theme.text.primary }]}>Import document</Text>
            <Text style={[styles.modalHint, { color: theme.text.secondary }]}>
              Choose how the PDF or Word file should be added to the writer.
            </Text>
            <TouchableOpacity
              style={[styles.importChoiceBtn, glassStyles.cardInset]}
              onPress={() => void runDocumentImport('plain')}
            >
              <Ionicons name="document-text-outline" size={18} color={theme.brand.primary} />
              <View style={styles.importChoiceCopy}>
                <Text style={[styles.importChoiceTitle, { color: theme.text.primary }]}>Import as-is</Text>
                <Text style={[styles.importChoiceHint, { color: theme.text.secondary }]}>
                  Fast import with basic paragraph and list formatting.
                </Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.importChoiceBtn, glassStyles.cardInset, { borderColor: `${theme.brand.primary}55` }]}
              onPress={() => void runDocumentImport('ai')}
            >
              <Ionicons name="sparkles-outline" size={18} color={theme.brand.primary} />
              <View style={styles.importChoiceCopy}>
                <Text style={[styles.importChoiceTitle, { color: theme.text.primary }]}>Reformat with AI</Text>
                <Text style={[styles.importChoiceHint, { color: theme.text.secondary }]}>
                  Analyze the rider and rewrite it using VRC headings, links, and layout.
                </Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={styles.modalBtnGhost} onPress={() => setImportChooserOpen(false)}>
              <Text style={{ color: theme.text.secondary, fontWeight: '700' }}>Cancel</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function WriterInputModal({
  visible,
  title,
  fields,
  confirmLabel,
  confirmDisabled = false,
  theme,
  onClose,
  onChange,
  onConfirm,
}) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={modalStyles.backdrop} onPress={onClose}>
        <Pressable style={[modalStyles.card, { backgroundColor: theme.background.card, borderColor: theme.ui.border }]} onPress={() => {}}>
          <Text style={[modalStyles.title, { color: theme.text.primary }]}>{title}</Text>
          {fields.map((field) => (
            <View key={field.key} style={{ gap: 6 }}>
              <Text style={[modalStyles.label, { color: theme.text.secondary }]}>{field.label}</Text>
              <TextInput
                value={field.value}
                onChangeText={(next) => onChange(field.key, next)}
                placeholder={field.placeholder}
                placeholderTextColor={theme.input.placeholder}
                multiline={field.multiline}
                autoCapitalize="none"
                keyboardType={field.key === 'url' ? 'url' : field.key === 'emails' ? 'email-address' : 'default'}
                style={[
                  modalStyles.input,
                  field.multiline && modalStyles.inputMultiline,
                  {
                    color: theme.input.text,
                    borderColor: theme.ui.border,
                    backgroundColor: theme.background.secondary,
                  },
                ]}
              />
            </View>
          ))}
          <View style={modalStyles.actions}>
            <TouchableOpacity style={modalStyles.btnGhost} onPress={onClose}>
              <Text style={{ color: theme.text.secondary, fontWeight: '700' }}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[modalStyles.btnPrimary, { backgroundColor: theme.brand.primary }, confirmDisabled && { opacity: 0.6 }]}
              onPress={onConfirm}
              disabled={confirmDisabled}
            >
              <Text style={{ color: theme.button.primaryText, fontWeight: '800' }}>{confirmLabel}</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function createStyles(theme, glassStyles) {
  return StyleSheet.create({
    wrap: { gap: 10, width: '100%' },
    actionRow: { flexDirection: 'row', gap: 8 },
    actionBtn: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      ...glassStyles.cardInset,
      paddingVertical: 9,
    },
    actionBtnText: { fontSize: 11, fontWeight: '800', color: theme.brand.primary },
    modeRow: { flexDirection: 'row', gap: 8 },
    modeBtn: {
      flex: 1,
      ...glassStyles.cardInset,
      paddingVertical: 10,
      paddingHorizontal: 10,
      gap: 4,
    },
    modeBtnActive: {
      borderColor: theme.brand.primary,
      backgroundColor: `${theme.brand.primary}14`,
    },
    modeBtnText: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.text.primary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    modeBtnTextActive: { color: theme.brand.primary },
    modeBtnHint: { fontSize: 11, lineHeight: 14, color: theme.text.secondary },
    modeBtnHintActive: { color: theme.text.primary },
    lead: { fontSize: 12, lineHeight: 16, color: theme.text.secondary },
    toolbarWrap: { width: '100%' },
    toolbar: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 4,
      width: '100%',
    },
    toolBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3,
      borderRadius: 7,
      ...glassStyles.cardInset,
      paddingVertical: 5,
      paddingHorizontal: 7,
      minHeight: 30,
    },
    toolBtnIconOnly: {
      width: 30,
      paddingHorizontal: 0,
    },
    toolBtnText: { fontSize: 11, fontWeight: '700', color: theme.text.primary },
    toolBtnTextEm: { fontStyle: 'italic' },
    importStatus: { fontSize: 12, color: theme.brand.primary, fontWeight: '700' },
    shortcutHint: { fontSize: 10, color: theme.text.tertiary, marginTop: -4 },
    partySection: { gap: 6 },
    sectionLabel: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    sectionHint: { fontSize: 12, color: theme.text.secondary },
    partyScroll: { gap: 6, paddingVertical: 2 },
    insertedMemberList: { gap: 4, marginTop: 4 },
    scopeWrap: {
      marginTop: 14,
      paddingTop: 12,
      paddingHorizontal: 10,
      paddingBottom: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
      minWidth: 0,
      overflow: 'hidden',
    },
    modalBackdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    modalCard: {
      width: '100%',
      maxWidth: 420,
      borderRadius: 14,
      borderWidth: 1,
      padding: 18,
      gap: 14,
    },
    modalTitle: { fontSize: 18, fontWeight: '800' },
    modalHint: { fontSize: 13, lineHeight: 18 },
    switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    switchLabel: { fontSize: 14, fontWeight: '600', flex: 1, paddingRight: 12 },
    modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10 },
    modalBtnGhost: { paddingVertical: 10, paddingHorizontal: 14 },
    modalBtnPrimary: { paddingVertical: 10, paddingHorizontal: 16, borderRadius: 8 },
    importChoiceBtn: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 10,
      padding: 12,
    },
    importChoiceCopy: { flex: 1, gap: 4 },
    importChoiceTitle: { fontSize: 14, fontWeight: '800' },
    importChoiceHint: { fontSize: 12, lineHeight: 16 },
  });
}

const modalStyles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 14,
    borderWidth: 1,
    padding: 18,
    gap: 12,
  },
  title: { fontSize: 18, fontWeight: '800' },
  label: { fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.4 },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  inputMultiline: { minHeight: 72, textAlignVertical: 'top' },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 4 },
  btnGhost: { paddingVertical: 10, paddingHorizontal: 14 },
  btnPrimary: { paddingVertical: 10, paddingHorizontal: 16, borderRadius: 8 },
});

export default CalendarWriterPanel;
